import type { MoveAnalysis } from './review-engine';

export interface MistakePattern {
  type: 'blunder' | 'mistake' | 'inaccuracy' | 'missed_win' | 'missed_tactic';
  category: 'tactical' | 'positional' | 'endgame' | 'opening' | 'middlegame';
  frequency: number;
  averageLoss: number;
  positions: string[]; // FEN strings
}

export interface TrainingPosition {
  fen: string;
  correctMove: string;
  correctMoveSan: string;
  playerColor: 'white' | 'black';
  mistakeType: string;
  category: string;
  difficulty: number;
  evaluationLoss: number;
  context?: string;
}

/**
 * Analyzes moves to identify patterns in mistakes
 */
export function analyzeMistakePatterns(
  moves: MoveAnalysis[],
  playerColor: 'white' | 'black'
): MistakePattern[] {
  const patterns: Map<string, MistakePattern> = new Map();

  moves.forEach((move, index) => {
    const moveColor = index % 2 === 0 ? 'white' : 'black';
    if (moveColor !== playerColor) return;
    
    if (['blunder', 'mistake', 'inaccuracy'].includes(move.label)) {
      const mistakeType = move.label;
      const category = categorizePosition(index + 1, '');
      const key = `${mistakeType}-${category}`;
      
      const existing = patterns.get(key);
      if (existing) {
        existing.frequency++;
        existing.averageLoss = (existing.averageLoss * (existing.frequency - 1) + move.centipawnLoss) / existing.frequency;
        existing.positions.push('');
      } else {
        patterns.set(key, {
          type: mistakeType as any,
          category,
          frequency: 1,
          averageLoss: move.centipawnLoss,
          positions: ['']
        });
      }
    }
  });

  return Array.from(patterns.values()).sort((a, b) => b.frequency - a.frequency);
}

/**
 * Categorizes a position based on move number and FEN characteristics
 */
export function categorizePosition(moveNumber: number, fen: string): 'tactical' | 'positional' | 'endgame' | 'opening' | 'middlegame' {
  // Count pieces to determine game phase
  const pieceCount = (fen.match(/[pnbrqkPNBRQK]/g) || []).length;
  
  if (moveNumber <= 15) return 'opening';
  if (pieceCount <= 10) return 'endgame';
  if (pieceCount <= 20) return 'middlegame';
  
  // Heuristic: lots of pieces suggests tactical, fewer suggests positional
  return pieceCount > 24 ? 'tactical' : 'positional';
}

/**
 * Extracts training positions from move analysis
 */
export function extractTrainingPositions(
  moves: MoveAnalysis[],
  playerColor: 'white' | 'black',
  minCentipawnLoss: number = 50
): TrainingPosition[] {
  const positions: TrainingPosition[] = [];

  moves.forEach((move, index) => {
    const moveColor = index % 2 === 0 ? 'white' : 'black';
    if (moveColor !== playerColor) return;
    
    if (['blunder', 'mistake', 'inaccuracy'].includes(move.label) && 
        move.centipawnLoss >= minCentipawnLoss && 
        move.bestMove) {
      
      const mistakeType = move.label;
      const category = categorizePosition(index + 1, '');
      
      positions.push({
        fen: '',
        correctMove: move.bestMove,
        correctMoveSan: move.bestMove,
        playerColor,
        mistakeType,
        category,
        difficulty: calculateDifficulty(move.centipawnLoss, category),
        evaluationLoss: move.centipawnLoss,
        context: `Move ${index + 1}: You played ${move.move} but ${move.bestMove} was better`
      });
    }
  });

  return positions;
}

/**
 * Calculates difficulty rating based on centipawn loss and position type
 */
function calculateDifficulty(centipawnLoss: number, category: string): number {
  let baseDifficulty = 1500;
  
  // Higher centipawn loss = harder to spot the correct move
  baseDifficulty += Math.min(centipawnLoss / 2, 500);
  
  // Tactical positions are generally easier to calculate
  if (category === 'tactical') baseDifficulty -= 100;
  if (category === 'endgame') baseDifficulty -= 50;
  if (category === 'positional') baseDifficulty += 100;
  
  return Math.max(800, Math.min(3000, Math.round(baseDifficulty)));
}

/**
 * Prioritizes training positions based on impact and frequency
 */
export function prioritizeTrainingPositions(
  patterns: MistakePattern[],
  userElo: number = 1500
): MistakePattern[] {
  return patterns.sort((a, b) => {
    // Weight: frequency * average loss * difficulty modifier
    const scoreA = a.frequency * a.averageLoss * getDifficultyModifier(a.category, userElo);
    const scoreB = b.frequency * b.averageLoss * getDifficultyModifier(b.category, userElo);
    return scoreB - scoreA;
  });
}

/**
 * Gets difficulty modifier based on user ELO and position category
 */
function getDifficultyModifier(category: string, userElo: number): number {
  if (userElo < 1200) {
    return category === 'tactical' ? 1.5 : 1.0;
  } else if (userElo < 1800) {
    return category === 'middlegame' ? 1.3 : 1.0;
  } else {
    return category === 'positional' ? 1.4 : 1.0;
  }
}

/**
 * Calculates spaced repetition interval using SM-2 algorithm
 */
export function calculateNextReviewDate(
  isCorrect: boolean,
  currentEaseFactor: number,
  currentInterval: number = 1
): { nextDate: Date; newEaseFactor: number; newInterval: number } {
  let newEaseFactor = currentEaseFactor;
  let newInterval = currentInterval;
  
  if (isCorrect) {
    newInterval = currentInterval === 1 ? 6 : Math.round(currentInterval * currentEaseFactor);
  } else {
    newEaseFactor = Math.max(1.3, currentEaseFactor - 0.2);
    newInterval = 1;
  }
  
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + newInterval);
  
  return { nextDate, newEaseFactor, newInterval };
}

/**
 * Updates mastery level based on recent performance
 */
export function calculateMasteryLevel(
  timesAttempted: number,
  timesSolved: number
): number {
  if (timesAttempted === 0) return 0;
  
  const successRate = timesSolved / timesAttempted;
  
  if (timesAttempted < 3) return 0;
  if (successRate >= 0.9 && timesAttempted >= 5) return 5;
  if (successRate >= 0.8 && timesAttempted >= 4) return 4;
  if (successRate >= 0.7 && timesAttempted >= 3) return 3;
  if (successRate >= 0.6) return 2;
  if (successRate >= 0.4) return 1;
  
  return 0;
}
